<script>
import { mapGetters } from 'vuex';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import UpdateBanner from './components/app/UpdateBanner.vue';
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import PendingEmailVerificationBanner from './components/app/PendingEmailVerificationBanner.vue';
import vueActionCable from './helper/actionCable';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import { setColorTheme } from './helper/themeHelper';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import { useAccount } from 'dashboard/composables/useAccount';
import { useFontSize } from 'dashboard/composables/useFontSize';
import {
  registerSubscription,
  verifyServiceWorkerExistence,
} from './helper/pushHelper';
import ReconnectService from 'dashboard/helper/ReconnectService';
import { useUISettings } from 'dashboard/composables/useUISettings';

export default {
  name: 'App',

  components: {
    LoadingState,
    NetworkNotification,
    UpdateBanner,
    PaymentPendingBanner,
    WootSnackbarBox,
    PendingEmailVerificationBanner,
  },
  setup() {
    const router = useRouter();
    const store = useStore();
    const { accountId } = useAccount();
    // Use the font size composable (it automatically sets up the watcher)
    const { currentFontSize } = useFontSize();
    const { uiSettings } = useUISettings();

    return {
      router,
      store,
      currentAccountId: accountId,
      currentFontSize,
      uiSettings,
    };
  },
  data() {
    return {
      latestChatwootVersion: null,
      actionCable: null,
      reconnectService: null,
      initializedAccountId: null,
      initializedPubsubToken: null,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      isRTL: 'accounts/isRTL',
      currentUser: 'getCurrentUser',
      authUIFlags: 'getAuthUIFlags',
      accountUIFlags: 'accounts/getUIFlags',
    }),
    hideOnOnboardingView() {
      return !isOnOnboardingView(this.$route);
    },
    // The realtime subscription is keyed on BOTH the account and the user's
    // pubsub token, so the watcher has to react to either one arriving. It
    // used to watch the account id alone: when the account resolved before the
    // user did, initializeAccount ran once with no token and never ran again.
    realtimeIdentity() {
      return `${this.currentAccountId}:${this.currentUser?.pubsub_token || ''}`;
    },
  },

  watch: {
    realtimeIdentity: {
      immediate: true,
      handler() {
        if (this.currentAccountId) {
          this.initializeAccount();
        }
      },
    },
  },
  mounted() {
    this.initializeColorTheme();
    this.listenToThemeChanges();
    // If user locale is set, use it; otherwise use account locale
    this.setLocale(
      this.uiSettings?.locale || window.chatwootConfig.selectedLocale
    );
  },
  unmounted() {
    this.cleanupRealtimeServices();
  },
  methods: {
    cleanupRealtimeServices() {
      const previousReconnectService = this.reconnectService;

      if (previousReconnectService) {
        previousReconnectService.disconnect();
        this.reconnectService = null;
      }

      if (this.actionCable) {
        this.actionCable.disconnect();
        this.actionCable = null;
      }

      if (window.reconnectService === previousReconnectService) {
        window.reconnectService = null;
      }

      this.initializedAccountId = null;
      this.initializedPubsubToken = null;
    },
    initializeColorTheme() {
      setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = e => setColorTheme(e.matches);
    },
    setLocale(locale) {
      this.$root.$i18n.locale = locale;
    },
    async initializeAccount() {
      await this.$store.dispatch('accounts/get');
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      const { locale, latest_chatwoot_version: latestChatwootVersion } =
        this.getAccount(this.currentAccountId);
      const { pubsub_token: pubsubToken } = this.currentUser || {};

      const isRealtimeAlreadyInitialized =
        this.actionCable &&
        this.reconnectService &&
        this.initializedAccountId === this.currentAccountId &&
        this.initializedPubsubToken === pubsubToken;

      // If user locale is set, use it; otherwise use account locale
      this.setLocale(this.uiSettings?.locale || locale);
      this.latestChatwootVersion = latestChatwootVersion;

      if (isRealtimeAlreadyInitialized) {
        return;
      }

      // Never open the cable without a token. RoomChannel resolves the user
      // FROM pubsub_token, and with it missing `ContactInbox.find_by!` raises
      // inside `subscribed` - ActionCable then discards the subscription
      // without sending a rejection. The result is the worst possible shape:
      // a socket that connects, stays open and keeps exchanging pings while
      // delivering exactly zero events. Nothing looks broken, no error is
      // logged client-side, and the dashboard only ever updates on F5.
      // Verified against production: an identifier carrying account_id and
      // user_id gets `confirm_subscription` and a stream of events; the same
      // token without user_id gets `welcome` and pings and nothing else.
      // Bailing here is safe because realtimeIdentity re-runs this the moment
      // the token lands.
      if (!pubsubToken) {
        return;
      }

      this.cleanupRealtimeServices();
      this.actionCable = vueActionCable.init(this.store, pubsubToken);
      this.reconnectService = new ReconnectService(this.store, this.router);
      this.initializedAccountId = this.currentAccountId;
      this.initializedPubsubToken = pubsubToken;
      window.reconnectService = this.reconnectService;

      verifyServiceWorkerExistence(registration =>
        registration.pushManager.getSubscription().then(subscription => {
          if (subscription) {
            registerSubscription();
          }
        })
      );
    },
  },
};
</script>

<template>
  <div
    v-if="!authUIFlags.isFetching && !accountUIFlags.isFetchingItem"
    id="app"
    class="flex flex-col w-full h-screen min-h-0 bg-n-background"
    :dir="isRTL ? 'rtl' : 'ltr'"
  >
    <UpdateBanner :latest-chatwoot-version="latestChatwootVersion" />
    <template v-if="currentAccountId">
      <PendingEmailVerificationBanner v-if="hideOnOnboardingView" />
      <PaymentPendingBanner v-if="hideOnOnboardingView" />
    </template>
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
    <WootSnackbarBox />
    <NetworkNotification />
  </div>
  <LoadingState v-else />
</template>

<style lang="scss">
@import './assets/scss/app';

.v-popper--theme-tooltip .v-popper__inner {
  background: black !important;
  font-size: 0.75rem;
  padding: 4px 8px !important;
  border-radius: 6px;
  font-weight: 400;
}

.v-popper--theme-tooltip .v-popper__arrow-container {
  display: none;
}

.multiselect__input {
  margin-bottom: 0px !important;
}
</style>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>
